import van from "../../vanjs.mjs";
import { Button } from "../elements/buttons.mjs";
import { MainPanel } from "./mainPanel.mjs";
import { Menu } from "./menu.mjs";
import { Modal } from "./modal.mjs";
import { FullscreenMenuNavTab } from "./fullscreenMenuNavTab.mjs";

const { div } = van.tags;

/**
 * Modal-backed fullscreen menu built from existing menu primitives.
 * It keeps the service logic out of the component and reuses MainPanel/Menu styling.
 */
export class FullscreenMenuPanel extends MainPanel {
    constructor(options = undefined) {
        const normalizedOptions = options || {};

        const width = normalizedOptions.width || "min(1120px, 96vw)";
        const heightClass = normalizedOptions.heightClass || "h-[min(78vh,56rem)] min-h-[28rem]";
        const defaultOrientation = normalizedOptions.orientation || Menu.ORIENTATION.LEFT;
        const defaultDesign = normalizedOptions.design || Menu.DESIGN.TITLEICON;
        const defaultButtonSide = normalizedOptions.buttonSide || Menu.BUTTONSIDE.LEFT;
        const defaultRounded = normalizedOptions.rounded || Menu.ROUNDED.ENABLE;

        super({
            ...normalizedOptions,
            namespacedTabs: normalizedOptions.namespacedTabs !== false,
            namespaces: normalizedOptions.namespaces || Menu.DEFAULT_NAMESPACES,
            defaultNamespace: normalizedOptions.defaultNamespace || Menu.NAMESPACE.SYSTEM,
            orientation: Menu.ORIENTATION.LEFT,
            buttonSide: Menu.BUTTONSIDE.LEFT,
            rounded: Menu.ROUNDED.ENABLE,
            bodyScroll: Menu.SCROLL.ENABLE,
            extraClasses: {
                ...(normalizedOptions.extraClasses || {}),
                display: "flex",
                height: "h-full",
                overflow: "overflow-hidden",
            }
        });

        this.width = width;
        this.heightClass = heightClass;
        this.defaultOrientation = defaultOrientation;
        this.defaultDesign = defaultDesign;
        this.defaultButtonSide = defaultButtonSide;
        this.defaultRounded = defaultRounded;
        this.headerTopPaddingClass = normalizedOptions.headerTopPaddingClass || "pt-3";
        this.closeButtonSide = normalizedOptions.closeButtonSide || Modal.CLOSE_BUTTON_SIDE.LEFT;
        this.namespaceLabelClass = normalizedOptions.namespaceLabelClass || `px-3 text-xs font-semibold uppercase tracking-[0.16em] text-base-content/50 ${this.headerTopPaddingClass}`;
        this.namespaceSeparatorClass = normalizedOptions.namespaceSeparatorClass || "my-2 border-t border-base-300/70";
        this.namespaceGroupClass = normalizedOptions.namespaceGroupClass || "flex flex-col gap-1 min-w-0";
        this.namespaceButtonsClass = normalizedOptions.namespaceButtonsClass || "flex min-w-0 gap-1";

        this.bodyRoot = div({
            class: `${this.heightClass} min-h-0 w-full min-w-0 bg-base-200`
        });

        this.modal = new Modal({
            id: `${this.id}-modal`,
            body: [this.bodyRoot],
            width: this.width,
            isBlocking: true,
            allowClose: true,
            allowResize: false,
            borderLess: true,
            closeButtonSide: this.closeButtonSide,
        });

        this._mountedMenu = false;
        this._created = false;

        // Sidebar tone: one step apart from the content column (bodyRoot is
        // bg-base-200) so the selected nav button — which carries bg-base-200,
        // see FullscreenMenuNavTab — visually joins the opened page.
        // The Join base carries `bg-join` (custom.css, loaded AFTER tailwind,
        // so it would override any bg-* utility) — drop it for this sidebar.
        this.header.setClass("base", "join");
        this.header.setClass("closeButtonPadding", "mt-7 bg-base-300");
        this.body.setClass("fullscreenMenuBodyPadding", "px-3");
    }

    create() {
        if (!this._mountedMenu) {
            const menuRoot = super.create();
            if (menuRoot && !this.bodyRoot.contains(menuRoot)) {
                van.add(this.bodyRoot, menuRoot);
            }
            this._mountedMenu = true;
        }

        const root = this.modal.create();
        // Keep the root for header lookups: during create() the modal is not
        // yet in the document, so document.getElementById-based resolution
        // (see _resolveHeaderElement) would silently skip all create-time
        // layout/state syncs until the first window resize.
        this._modalRoot = root;
        this._decorateModal(root);
        this._applyMenuDefaults();
        this._renderNamespacedHeader(root);
        this._created = true;
        return root;
    }

    _decorateModal(root) {
        const box = root?.querySelector?.(".modal-box");
        if (box) {
            box.style.maxWidth = "none";
            box.style.width = this.width;
        }

        const body = root?.querySelector?.(".modal-body");
        if (body) {
            body.classList.add("overflow-hidden", "bg-transparent", "p-0", "rounded-none");
        }
    }

    _renderNamespacedHeader(root = undefined) {
        if (!this.usesNamespacedTabs?.()) return;

        const header = this._resolveHeaderElement(root);
        if (!header) return;

        const buttonNodes = this._collectHeaderButtonNodes(header);
        if (!buttonNodes.length) return;

        const buttonsByTabId = new Map(buttonNodes.map(node => [node.dataset.menuTabId, node]));
        const orderedNamespaces = this.getNamespacesWithTabs?.() || [];
        const nodes = [];
        let firstNamespace = true;

        for (const namespace of orderedNamespaces) {
            const tabs = namespace.tabs?.filter(tab => buttonsByTabId.has(tab.id)) || [];
            if (!tabs.length) continue;

            if (!firstNamespace) {
                const separator = document.createElement("div");
                separator.className = this.namespaceSeparatorClass;
                separator.dataset.menuNamespaceSeparator = namespace.id;
                nodes.push(separator);
            }

            const group = document.createElement("div");
            group.className = this.namespaceGroupClass;
            group.dataset.menuNamespaceGroup = namespace.id;

            const label = document.createElement("div");
            label.className = this.namespaceLabelClass;
            label.dataset.menuNamespaceLabel = namespace.id;
            label.textContent = namespace.title || namespace.label || namespace.id;
            group.appendChild(label);

            const buttonRow = document.createElement("div");
            buttonRow.className = this.namespaceButtonsClass;
            buttonRow.dataset.menuNamespaceButtons = namespace.id;

            for (const tab of tabs) {
                const buttonNode = buttonsByTabId.get(tab.id);
                if (buttonNode) {
                    buttonRow.appendChild(buttonNode);
                }
            }

            group.appendChild(buttonRow);
            nodes.push(group);

            firstNamespace = false;
        }

        if (nodes.length) {
            header.replaceChildren(...nodes);
        }

        this._syncHeaderButtonOrientation();
        this._syncNamespaceGroupOrientation();
    }

    _syncNamespaceGroupOrientation() {
        const header = this._resolveHeaderElement();
        if (!header) return;

        // Use the collapse verdict cached by the last _syncLayout pass rather than
        // recomputing it here — recomputing measures a DOM _syncLayout just mutated
        // and can flip, yielding a hybrid layout (side-by-side + horizontal buttons).
        // Fall back to a fresh measure only before the first sync has run.
        const collapsedToTop = this._collapsedToTop !== undefined
            ? this._collapsedToTop
            : (this._shouldCollapseSideHeader && this._shouldCollapseSideHeader());
        const stackButtons = this._isSideOrientation && this._isSideOrientation() && !collapsedToTop;

        // Collapsed horizontal strip: groups must keep their intrinsic width and
        // let the header scroll (it is overflow-x:auto exactly for this). The
        // `min-w-0` they carry for the sidebar would otherwise let flex shrink
        // them below content, so button rows overflow their group box and the
        // next group paints over them (overlapping category buttons on narrow
        // windows). Sidebar mode restores shrink so long titles clip.
        for (const group of header.querySelectorAll("[data-menu-namespace-group]")) {
            group.style.flexShrink = stackButtons ? "" : "0";
        }
        // The separator is a `border-t` horizontal rule for the stacked sidebar —
        // in a row it is a zero-width invisible div. Turn it into a vertical rule
        // spanning the strip (inline styles: the shipped Tailwind build is purged,
        // so border-l/self-stretch utilities may be missing).
        for (const separator of header.querySelectorAll("[data-menu-namespace-separator]")) {
            separator.style.borderTopWidth = stackButtons ? "" : "0";
            separator.style.borderLeftWidth = stackButtons ? "" : "1px";
            separator.style.alignSelf = stackButtons ? "" : "stretch";
            separator.style.margin = stackButtons ? "" : "0 0.125rem";
            separator.style.flexShrink = "0";
        }

        for (const buttonRow of header.querySelectorAll("[data-menu-namespace-buttons]")) {
            buttonRow.style.flexDirection = stackButtons ? "column" : "row";
            buttonRow.style.flexWrap = "nowrap";
            buttonRow.style.alignItems = stackButtons ? "stretch" : "center";

            // Sidebar (stacked) mode: every nav button spans the full sidebar
            // width with left-aligned content, long titles clipped. In the
            // collapsed horizontal strip (narrow windows) buttons revert to
            // content width so the row keeps fitting.
            for (const btn of buttonRow.querySelectorAll("[data-menu-tab-id]")) {
                btn.style.width = stackButtons ? "100%" : "";
                btn.style.maxWidth = "100%";
                btn.style.justifyContent = "flex-start";
                btn.style.overflow = "hidden";
                // Re-assert the per-state background on the concrete node —
                // pre-mount _setFocus/_removeFocus calls could not reach it.
                this.tabs[btn.dataset.menuTabId]?._applyNavVisual?.(btn);
            }
        }
    }

    _resolveHeaderElement(root = undefined) {
        if (!this.header?.id) return null;
        return root?.querySelector?.(`#${this.header.id}`)
            || document.getElementById(this.header.id)
            || this._modalRoot?.querySelector?.(`#${this.header.id}`)
            || null;
    }

    _collectHeaderButtonNodes(header) {
        const tabOrder = Object.values(this.tabs).filter(tab => tab?.headerButton);
        const taggedButtons = Array.from(header.querySelectorAll?.("[data-menu-tab-id]") || []);
        const taggedIds = new Set(taggedButtons.map(node => node.dataset.menuTabId).filter(Boolean));
        const remainingTabs = tabOrder.filter(tab => !taggedIds.has(tab.id));
        let remainingIndex = 0;

        for (const child of Array.from(header.children)) {
            if (child.dataset?.menuNamespaceLabel
                || child.dataset?.menuNamespaceSeparator
                || child.dataset?.menuNamespaceGroup
                || child.dataset?.menuNamespaceButtons) {
                continue;
            }
            if (child.dataset?.menuTabId) {
                continue;
            }

            const tab = remainingTabs[remainingIndex++];
            if (!tab) continue;
            child.dataset.menuTabId = tab.id;
        }

        return Array.from(header.querySelectorAll?.("[data-menu-tab-id]") || []);
    }

    _applyMenuDefaults() {
        this.set(this.defaultOrientation, this.defaultButtonSide, this.defaultDesign, this.defaultRounded);
        this._syncHeaderButtonOrientation();
        this._syncNamespaceGroupOrientation();
    }

    _syncLayout() {
        super._syncLayout();
        this._syncNamespaceGroupOrientation();
    }

    _syncHeaderButtonOrientation() {
        for (const tab of Object.values(this.tabs)) {
            if (!tab?.headerButton) continue;
            tab.headerButton.set(Button.ORIENTATION.HORIZONTAL);
            tab.headerButton.setClass("fullscreenMenuButtonWidth", "justify-start flex-nowrap whitespace-nowrap");
        }
    }

    _ensureSingleOpen(id) {
        for (const [tabId, tab] of Object.entries(this.tabs)) {
            if (tabId === id) continue;
            tab._removeFocus?.();
        }

        const tab = this.getTab(id);
        if (!tab) return false;
        tab._setFocus?.();
        this._focused = id;
        return true;
    }

    addTab(item, componentId = undefined) {
        const normalized = {
            namespace: item.namespace || this.defaultNamespace || Menu.NAMESPACE.SYSTEM,
            ...item,
            class: item.class && item.class !== Menu ? item.class : undefined,
        };

        if (!normalized.class) {
            normalized.class = FullscreenMenuNavTab;
        }

        const tab = super.addTab(normalized, componentId);
        tab.close?.();
        this._syncHeaderButtonOrientation();
        if (this._mountedMenu) {
            this._renderNamespacedHeader();
        }

        if (!this._focused) {
            this._ensureSingleOpen(normalized.id);
        }
        return tab;
    }

    deleteTab(id) {
        super.deleteTab(id);
        if (this._mountedMenu) {
            this._renderNamespacedHeader();
        }
        return this;
    }

    setOrientation(orientation) {
        this.set(orientation);
        this._syncHeaderButtonOrientation();
        return this;
    }

    focus(id) {
        this.create();
        if (!this.getTab(id)) return false;
        this._ensureSingleOpen(id);
        this.open();
        return true;
    }

    open() {
        this.create();
        this.modal.open();
        return this;
    }

    close() {
        this.modal.close();
        return this;
    }
}
